import { motion } from "framer-motion";
import { Download, AppWindow, Share2, Banknote } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const STEPS = [
  {
    num: "01",
    icon: Download,
    title: "Download & install",
    text: "Grab the free Honeygain app on any device — Windows, macOS, Android, iOS or Linux. Setup takes under two minutes.",
    testid: "how-it-works-step-1",
  },
  {
    num: "02",
    icon: AppWindow,
    title: "Let it run",
    text: "The app works quietly in the background. No surveys, no watching ads, nothing to click. Just use your devices as usual.",
    testid: "how-it-works-step-2",
  },
  {
    num: "03",
    icon: Share2,
    title: "Share bandwidth",
    text: "Your unused internet is securely shared with vetted businesses for web research, price comparison and content delivery.",
    testid: "how-it-works-step-3",
  },
  {
    num: "04",
    icon: Banknote,
    title: "Get paid",
    text: "Earn credits for every GB shared, then cash out via PayPal or crypto once you hit $20. Plus a $3 head start with our link.",
    testid: "how-it-works-step-4",
  },
];

const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="py-20 sm:py-28 bg-white">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-700">How it works</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 max-w-2xl">
          Four steps between you and effortless income
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {STEPS.map(({ num, icon: Icon, title, text, testid }, i) => (
          <motion.div
            key={num}
            data-testid={testid}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -8 }}
            className={`group relative rounded-3xl border p-7 transition-shadow hover:shadow-xl hover:shadow-amber-900/5 ${
              i % 2 === 0 ? "bg-amber-50/60 border-amber-100" : "bg-[#FAF8F5] border-zinc-100"
            }`}
          >
            <span className="font-display text-5xl font-extrabold text-amber-400/80 group-hover:text-amber-500 transition-colors">
              {num}
            </span>
            <div className="mt-6 grid place-items-center w-11 h-11 rounded-2xl bg-zinc-900 text-amber-400">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-zinc-900">{title}</h3>
            <p className="mt-2.5 text-sm text-zinc-600 leading-relaxed">{text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default HowItWorks;
